// Bike comparator: "Comparar" checkboxes (blocks/wrb-compare-toggle.liquid), the floating tray,
// and the compare page (sections/wrb-compare.liquid at /collections/bicicletas?view=comparar&p=…).
// Selection lives in localStorage so it survives navigation; the page URL carries the handles so a
// comparison can be shared. Spec rows come from the "WRB · Ficha técnica" section (single source).
import { CartLinesUpdateEvent } from '@shopify/events';

const STORAGE_KEY = 'wrb-compare';
const MAX_ITEMS = 3;
const phone = window.matchMedia('(max-width: 749px)');
const limit = () => (phone.matches ? 2 : MAX_ITEMS);
const root = window.Shopify?.routes?.root ?? '/';
const comparePage = document.querySelector('[data-wrb-compare-page]');

const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

// Every bike title starts with "Bicicleta"; drop it where names sit side by side.
const shortTitle = (title) => String(title ?? '').replace(/^Bicicleta\s+/i, '');

const sized = (url, width) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${width}` : '');

function read() {
  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(list) ? list.filter((item) => item && item.handle).slice(0, MAX_ITEMS) : [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
  } catch {}
  render();
}

const compareUrl = (list) =>
  `${root}collections/bicicletas?view=comparar&p=${list.map((item) => encodeURIComponent(item.handle)).join(',')}`;

/* ---------- Checkboxes ---------- */

function syncInputs() {
  const handles = new Set(read().map((item) => item.handle));
  document.querySelectorAll('[data-wrb-compare]').forEach((input) => {
    input.checked = handles.has(input.dataset.wrbCompare);
  });
}

document.addEventListener('change', (event) => {
  const input = event.target instanceof Element ? event.target.closest('[data-wrb-compare]') : null;
  if (!input) return;

  const list = read().filter((item) => item.handle !== input.dataset.wrbCompare);
  if (input.checked) {
    if (list.length >= limit()) {
      input.checked = false;
      flash(`Puedes comparar hasta ${limit()} bicicletas. Quita una para agregar otra.`);
      return;
    }
    list.push({ handle: input.dataset.wrbCompare, title: input.dataset.title, image: input.dataset.image });
  }
  write(list);
});

/* ---------- Floating tray ---------- */

let tray;
let flashTimer;

function buildTray() {
  tray = document.createElement('aside');
  tray.className = 'wrb-ctray';
  tray.setAttribute('aria-label', 'Bicicletas para comparar');
  tray.innerHTML = `
    <div class="wrb-ctray__panel">
      <ul class="wrb-ctray__items" role="list"></ul>
      <div class="wrb-ctray__actions">
        <button type="button" class="wrb-ctray__clear">Limpiar</button>
        <a class="wrb-ctray__go"></a>
      </div>
      <p class="wrb-ctray__msg" role="status"></p>
    </div>`;
  tray.querySelector('.wrb-ctray__clear').addEventListener('click', () => write([]));
  tray.querySelector('.wrb-ctray__items').addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-remove]') : null;
    if (button) write(read().filter((item) => item.handle !== button.dataset.remove));
  });
  tray.querySelector('.wrb-ctray__go').addEventListener('click', (event) => {
    if (event.currentTarget.getAttribute('aria-disabled') === 'true') event.preventDefault();
  });
  document.body.append(tray);
}

function renderTray() {
  if (comparePage) return;
  const list = read();
  if (!tray) {
    if (!list.length) return;
    buildTray();
  }

  const slots = Math.max(limit(), list.length);
  const items = [];
  for (let i = 0; i < slots; i++) {
    const item = list[i];
    items.push(
      item
        ? `<li class="wrb-ctray__item">
            <img src="${esc(item.image)}" alt="" width="48" height="48" loading="lazy">
            <span class="wrb-ctray__name">${esc(shortTitle(item.title))}</span>
            <button type="button" class="wrb-ctray__remove" data-remove="${esc(item.handle)}" aria-label="Quitar ${esc(item.title)}">
              <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>
            </button>
          </li>`
        : `<li class="wrb-ctray__item wrb-ctray__item--empty">Agrega otra bici</li>`
    );
  }
  tray.querySelector('.wrb-ctray__items').innerHTML = items.join('');

  const go = tray.querySelector('.wrb-ctray__go');
  go.href = compareUrl(list);
  const ready = list.length >= 2;
  go.setAttribute('aria-disabled', String(!ready));
  go.textContent = ready ? `Comparar (${list.length})` : 'Elige otra bici';

  const open = list.length > 0;
  tray.classList.toggle('is-open', open);
  tray.inert = !open;
  document.body.style.setProperty('--wrb-ctray-h', open ? `${tray.offsetHeight}px` : '0px');
}

function flash(message) {
  if (comparePage) return;
  if (!tray) buildTray();
  const msg = tray.querySelector('.wrb-ctray__msg');
  msg.textContent = message;
  clearTimeout(flashTimer);
  flashTimer = setTimeout(() => (msg.textContent = ''), 4000);
}

function render() {
  syncInputs();
  renderTray();
}

// Cards re-render after filtering/pagination: keep new checkboxes in sync (cheap check, rAF-batched).
let syncQueued = false;
new MutationObserver(() => {
  if (syncQueued) return;
  syncQueued = true;
  requestAnimationFrame(() => {
    syncQueued = false;
    syncInputs();
  });
}).observe(document.body, { childList: true, subtree: true });

window.addEventListener('storage', (event) => event.key === STORAGE_KEY && render());
window.addEventListener('pageshow', render);
phone.addEventListener('change', render);
render();

/* ---------- Compare page ---------- */

if (comparePage) initComparePage(comparePage);

function formatMoney(cents, format) {
  const value = Number(cents) / 100;
  const group = (amount, decimals, thousands, decimal) => {
    const [whole, fraction] = amount.toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return group(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return group(value, 2, "'", '.');
      default:
        return group(value, 2, ',', '.');
    }
  });
}

async function loadBike(handle) {
  const base = `${root}products/${encodeURIComponent(handle)}`;
  try {
    const [productRes, specsRes] = await Promise.all([fetch(`${base}.js`), fetch(`${base}?section_id=wrb-product-specs`)]);
    if (!productRes.ok) return null;
    const product = await productRes.json();
    const specs = [];
    if (specsRes.ok) {
      const html = new DOMParser().parseFromString(await specsRes.text(), 'text/html');
      html.querySelectorAll('.wrb-specs__table tbody tr').forEach((row) => {
        const label = row.querySelector('th')?.textContent.trim();
        const value = row.querySelector('td')?.textContent.trim();
        if (label && value) specs.push({ label, value });
      });
    }
    const first = product.variants.find((variant) => variant.available) ?? product.variants[0];
    return { product, specs, selected: [...first.options] };
  } catch {
    return null;
  }
}

// Spec rows keep the ficha técnica order: each bike's labels are a subsequence of the same fixed list.
function mergeLabels(bikes) {
  const labels = [];
  bikes.forEach(({ specs }) => {
    let insertAt = 0;
    specs.forEach(({ label }) => {
      const index = labels.indexOf(label);
      if (index === -1) labels.splice(insertAt++, 0, label);
      else insertAt = index + 1;
    });
  });
  return labels;
}

const variantFor = (product, selected) =>
  product.variants.find((variant) => variant.options.every((value, i) => value === selected[i]));

async function initComparePage(page) {
  const config = JSON.parse(page.querySelector('[data-wrb-compare-config]').textContent);
  const status = page.querySelector('[data-wrb-compare-status]');
  const table = page.querySelector('[data-wrb-compare-table]');
  const diffToggle = page.querySelector('[data-wrb-compare-diff]');
  const money = (cents) => formatMoney(cents, config.moneyFormat);

  const fromUrl = (new URLSearchParams(location.search).get('p') ?? '').split(',').map((h) => h.trim());
  let handles = fromUrl.filter(Boolean);
  if (!handles.length) handles = read().map((item) => item.handle);
  handles = [...new Set(handles)].slice(0, MAX_ITEMS);

  let bikes = (await Promise.all(handles.map(loadBike))).filter(Boolean);

  const save = () => {
    write(bikes.map(({ product }) => ({ handle: product.handle, title: product.title, image: sized(product.featured_image, 160) })));
    const params = new URLSearchParams(location.search);
    params.delete('p');
    const query = [params.toString(), `p=${bikes.map(({ product }) => encodeURIComponent(product.handle)).join(',')}`].filter(Boolean).join('&');
    history.replaceState(history.state, '', `${location.pathname}?${query}`);
  };

  const priceHtml = (variant) =>
    variant.compare_at_price > variant.price
      ? `<s class="wrb-ctable__was"><span class="visually-hidden">Antes </span>${money(variant.compare_at_price)}</s> <span class="wrb-ctable__now">${money(variant.price)}</span>`
      : `<span class="wrb-ctable__now">${money(variant.price)}</span>`;

  const buyHtml = (bike, i) => {
    const { product, selected } = bike;
    const variant = variantFor(product, selected);
    const selects = product.options
      .map((option, o) => {
        if (option.values.length < 2) return '';
        const choices = option.values
          .map((value) => {
            const ok = product.variants.some(
              (v) => v.available && v.options.every((val, j) => (j === o ? val === value : val === selected[j]))
            );
            return `<option value="${esc(value)}"${value === selected[o] ? ' selected' : ''}${ok ? '' : ' disabled'}>${esc(value)}${ok ? '' : ' · No disponible'}</option>`;
          })
          .join('');
        return `<label class="wrb-ctable__option"><span>${esc(option.name)}</span><select data-bike="${i}" data-option="${o}">${choices}</select></label>`;
      })
      .join('');
    const available = Boolean(variant?.available);
    return `
      ${selects}
      <button type="button" class="wrb-ctable__add" data-add="${i}"${available ? '' : ' disabled'}>${available ? 'Agregar al carrito' : 'No disponible'}</button>
      <p class="wrb-ctable__added" data-added="${i}" role="status"></p>
      <a class="wrb-ctable__view" href="${esc(product.url)}${variant ? `?variant=${variant.id}` : ''}">Ver producto</a>`;
  };

  function renderTable() {
    if (!bikes.length) {
      status.hidden = true;
      table.innerHTML = '';
      page.querySelector('[data-wrb-compare-empty]').hidden = false;
      diffToggle.closest('label').hidden = true;
      return;
    }

    const showAdd = bikes.length < limit();
    const cols = bikes.length + (showAdd ? 1 : 0);
    const labels = mergeLabels(bikes);
    const blank = showAdd ? '<td role="cell" class="wrb-ctable__slot"></td>' : '';
    const valueOf = (bike, label) => bike.specs.find((s) => s.label === label)?.value ?? '';

    const specRows = labels
      .map((label) => {
        const values = bikes.map((bike) => valueOf(bike, label));
        const same = values.every((value) => value.toLowerCase() === values[0].toLowerCase());
        return `<tr role="row" class="wrb-ctable__spec"${same && bikes.length > 1 ? ' data-same' : ''}>
          <th role="rowheader" scope="row">${esc(label)}</th>
          ${values.map((value) => `<td role="cell">${value ? esc(value) : '<span class="wrb-ctable__none" aria-label="Sin dato">—</span>'}</td>`).join('')}
          ${blank}
        </tr>`;
      })
      .join('');

    table.innerHTML = `
      <table role="table" class="wrb-ctable" style="--cols: ${cols}">
        <caption class="visually-hidden">Comparación de ${esc(bikes.map(({ product }) => product.title).join(', '))}</caption>
        <thead role="rowgroup">
          <tr role="row" class="wrb-ctable__media">
            <td role="cell" class="wrb-ctable__corner"></td>
            ${bikes
              .map(
                ({ product }, i) => `<td role="cell">
                  <div class="wrb-ctable__figure">
                    <a href="${esc(product.url)}" tabindex="-1" aria-hidden="true"><img src="${esc(sized(product.featured_image, 480))}" alt="" loading="lazy"></a>
                    <button type="button" class="wrb-ctable__remove" data-remove-bike="${i}" aria-label="Quitar ${esc(product.title)} de la comparación">
                      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>
                    </button>
                  </div>
                </td>`
              )
              .join('')}
            ${
              showAdd
                ? `<td role="cell" class="wrb-ctable__slot"><a class="wrb-ctable__add-slot" href="${root}collections/bicicletas">
                    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>
                    ${bikes.length === 1 ? 'Agrega otra bicicleta para comparar' : 'Agregar bicicleta'}
                  </a></td>`
                : ''
            }
          </tr>
        </thead>
        <tbody role="rowgroup">
          <tr role="row" class="wrb-ctable__names">
            <td role="cell" class="wrb-ctable__corner"></td>
            ${bikes
              .map(({ product, selected }) => {
                const variant = variantFor(product, selected) ?? product.variants[0];
                return `<th role="columnheader" scope="col">
                  <span class="wrb-ctable__vendor">${esc(product.vendor)}</span>
                  <a class="wrb-ctable__name" href="${esc(product.url)}">${esc(shortTitle(product.title))}</a>
                  <span class="wrb-ctable__price">${priceHtml(variant)}</span>
                </th>`;
              })
              .join('')}
            ${blank}
          </tr>
          <tr role="row" class="wrb-ctable__buy">
            <th role="rowheader" scope="row">Comprar</th>
            ${bikes.map((bike, i) => `<td role="cell">${buyHtml(bike, i)}</td>`).join('')}
            ${blank}
          </tr>
          ${specRows}
        </tbody>
      </table>
      ${labels.length ? '' : '<p class="wrb-compare__nospecs">Estas bicicletas aún no tienen ficha técnica cargada.</p>'}`;

    status.hidden = true;
    diffToggle.closest('label').hidden = bikes.length < 2 || !labels.length;
    table.classList.toggle('is-diff', diffToggle.checked);
  }

  table.addEventListener('change', (event) => {
    const select = event.target instanceof HTMLSelectElement ? event.target : null;
    if (!select) return;
    const bike = bikes[Number(select.dataset.bike)];
    const o = Number(select.dataset.option);
    bike.selected[o] = select.value;
    if (!variantFor(bike.product, bike.selected)?.available) {
      const fallback = bike.product.variants.find((v) => v.available && v.options[o] === select.value);
      if (fallback) bike.selected = [...fallback.options];
    }
    const focusKey = `[data-bike="${select.dataset.bike}"][data-option="${o}"]`;
    renderTable();
    table.querySelector(focusKey)?.focus();
  });

  table.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const remove = target?.closest('[data-remove-bike]');
    if (remove) {
      bikes.splice(Number(remove.dataset.removeBike), 1);
      save();
      renderTable();
      (table.querySelector('.wrb-ctable__remove') ?? diffToggle).focus?.();
      return;
    }
    const add = target?.closest('[data-add]');
    if (add) addToCart(add);
  });

  diffToggle.addEventListener('change', () => table.classList.toggle('is-diff', diffToggle.checked));

  async function addToCart(button) {
    const i = Number(button.dataset.add);
    const variant = variantFor(bikes[i].product, bikes[i].selected);
    const note = table.querySelector(`[data-added="${i}"]`);
    if (!variant?.available || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Agregando…';
    note.textContent = '';
    note.classList.remove('is-error');

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      });
      const data = await res.json();
      const cart = await (await fetch(`${root}cart.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: !res.ok, items: cart.items, source: 'wrb-compare', itemCount: 1, productId: String(bikes[i].product.id) },
      });
      if (!res.ok) throw new Error(data.description || data.message || 'No se pudo agregar');
      note.innerHTML = `Agregada al carrito · <a href="${root}cart">Ver carrito</a>`;
    } catch (error) {
      deferred.reject?.(error);
      note.classList.add('is-error');
      note.textContent = error instanceof Error && error.message ? error.message : 'No se pudo agregar. Intenta de nuevo.';
    } finally {
      button.removeAttribute('aria-busy');
      button.textContent = 'Agregar al carrito';
    }
  }

  if (bikes.length) save();
  renderTable();
  phone.addEventListener('change', renderTable);
}
